import Logger from '../utilities/logger';
import AbstractComponent from './abstract-component';
import A11yConsentApi from '../a11y-consent-api';

/**
 * Collapse for showing and hiding content.
 *
 * @author Philipp Seiler <sep@earlybird.at>
 */
export default class Collapse extends AbstractComponent {
  // Buttons showing/hiding the collapsible container
  private readonly buttons: HTMLButtonElement[];

  // Transition duration when showing/hiding the container
  private readonly transitionDuration: number = 250;

  // CSS transition style when showing/hiding the container
  private readonly transitionStyle: string = 'height 250ms;';

  // Whether reduced motion is active or not
  private readonly isReducedMotion: boolean;

  /**
   * @param {A11yConsentApi} api A11y Consent API
   * @param {HTMLElement} container Main container of the component
   * @param {HTMLButtonElement|NodeListOf<HTMLButtonElement>} buttons Button or buttons controlling the collapse
   * container
   */
  constructor(
    api: A11yConsentApi,
    container: HTMLElement,
    buttons: HTMLButtonElement | NodeListOf<HTMLButtonElement>
  ) {
    super(api, container);
    Logger.debug('collapse', 'Starting to construct.');

    this.buttons = buttons instanceof NodeList ? Array.from(buttons) : [buttons];
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.isReducedMotion = mediaQuery && mediaQuery.matches;

    if (this.buttons.length === 0) {
      if (!this.container.classList.contains('a11y-consent-collapse')) {
        Logger.warning('collapse', 'Collapse was tried to be constructed, but the collapse container did not have the' +
          '".a11y-consent-collapse" class. Stopping.');
        return;
      }
      Logger.warning('collapse', 'Collapse was tried to be constructed, but there were no buttons controlling the' +
        'collapse container.');
      return;
    }

    if (this.isReducedMotion) {
      Logger.info('collapse', 'Reduced motion is active.');
    } else {
      Logger.info('collapse', 'Reduced motion is not active.');
      this.container.classList.add('reduced-motion-inactive');
    }

    if (A11yConsentConfiguration.settings.settings.styles.collapse.transitionDuration) {
      this.transitionDuration = parseInt(A11yConsentConfiguration.settings.settings.styles.collapse.transitionDuration);
      Logger.info('collapse', `Set transition duration to ${this.transitionDuration}ms.`);
    }
    if (A11yConsentConfiguration.settings.settings.styles.collapse.transitionStyle) {
      this.transitionStyle = A11yConsentConfiguration.settings.settings.styles.collapse.transitionStyle;
      if (this.container.classList.contains('reduced-motion-inactive')) {
        this.container.style.transition = this.transitionStyle;
        Logger.info('collapse', `Set transition style to "${this.transitionStyle}".`);
      }
    }

    for (const button of this.buttons) {
      button.addEventListener('click', () => {
        this.toggle();
      });
    }

    Logger.debug('collapse', 'Finished constructing.');
  }

  /**
   * Shows the content of the collapsible.
   */
  public show(): void {
    this.emitter.emit('show', this);
    if (this.isReducedMotion) {
      this.container.classList.add('is-shown');
      this.container.ariaHidden = 'false';
      this.emitter.emit('shown', this);
    } else {
      this.container.classList.add('is-collapsing');

      // Calculate height to collapse to
      let height = 0;
      const child = this.container.children.item(0);
      if (child) {
        height = child.getBoundingClientRect().height;
      }

      // If for some reason no height could be determined, show content instantly
      if (height === 0) {
        this.container.classList.add('is-shown');
        this.container.style.height = 'auto';
        return;
      }

      // Slide open
      this.container.style.height = `${height}px`;
      setTimeout(() => {
        this.container.classList.remove('is-collapsing');
        this.container.classList.add('is-shown');
        this.container.ariaHidden = 'false';
        this.emitter.emit('shown', this);
      }, this.transitionDuration);
    }

    for (const button of this.buttons) {
      button.ariaExpanded = 'true';
      button.classList.add('is-shown');
    }
  }

  /**
   * Hides the content of the collapsible.
   */
  public hide(): void {
    this.emitter.emit('hide', this);
    if (this.isReducedMotion) {
      this.container.classList.remove('is-shown');
      this.container.ariaHidden = 'true';
      this.emitter.emit('hidden', this);
    } else {
      this.container.classList.remove('is-shown');
      this.container.classList.add('is-collapsing');

      // Slide close
      this.container.style.height = '0px';
      setTimeout(() => {
        this.container.classList.remove('is-collapsing');
        this.container.ariaHidden = 'true';
        this.emitter.emit('hidden', this);
      }, this.transitionDuration);
    }

    for (const button of this.buttons) {
      button.ariaExpanded = 'false';
      button.classList.remove('is-shown');
    }
  }

  /**
   * Toggles between showing and hiding the content of the collapsible.
   */
  public toggle(): void {
    this.container.ariaHidden === 'true' ? this.show() : this.hide();
  }
}
